<script setup lang="ts">
import { computed, ref } from "vue";
import { useRouter } from "vue-router";
import { useQuery, useQueryClient } from "@tanstack/vue-query";

import {
  archiveProject,
  createProject,
  deleteProject,
  listProjects,
  restoreProject,
} from "@/api/client";
import PageLayout from "@/components/PageLayout.vue";

const router = useRouter();
const queryClient = useQueryClient();

const { data: projects, isPending, isError, error } = useQuery({
  queryKey: ["projects"],
  queryFn: listProjects,
});

type Tab = "active" | "archived" | "all";
const tab = ref<Tab>("active");
const search = ref("");

const counts = computed(() => {
  const all = projects.value ?? [];
  return {
    active: all.filter((p) => p.status === "active").length,
    archived: all.filter((p) => p.status === "archived").length,
    all: all.length,
  };
});

const filtered = computed(() => {
  const q = search.value.trim().toLowerCase();
  return (projects.value ?? [])
    .filter((p) => (tab.value === "all" ? true : p.status === tab.value))
    .filter(
      (p) =>
        !q ||
        p.name.toLowerCase().includes(q) ||
        (p.code ?? "").toLowerCase().includes(q) ||
        (p.client_name ?? "").toLowerCase().includes(q),
    );
});

function enter(id: string): void {
  void router.push({ name: "project-dashboard", params: { projectId: id } });
}

// ---- new project ----
const showNew = ref(false);
const form = ref({ name: "", client_name: "", code: "" });
const creating = ref(false);
const formError = ref<string | null>(null);

async function create(): Promise<void> {
  if (!form.value.name.trim()) {
    formError.value = "A project name is required.";
    return;
  }
  creating.value = true;
  formError.value = null;
  try {
    const id = await createProject({
      name: form.value.name.trim(),
      client_name: form.value.client_name.trim() || undefined,
      code: form.value.code.trim() || undefined,
    });
    await queryClient.invalidateQueries({ queryKey: ["projects"] });
    await queryClient.invalidateQueries({ queryKey: ["me"] });
    enter(id);
  } catch (e) {
    formError.value = e instanceof Error ? e.message : String(e);
  } finally {
    creating.value = false;
  }
}

// ---- per-project menu ----
const openMenu = ref<string | null>(null);

async function act(
  fn: (id: string) => Promise<void>,
  id: string,
): Promise<void> {
  openMenu.value = null;
  await fn(id);
  await queryClient.invalidateQueries({ queryKey: ["projects"] });
  await queryClient.invalidateQueries({ queryKey: ["me"] });
}

async function onDelete(id: string, name: string): Promise<void> {
  openMenu.value = null;
  if (!window.confirm(`Delete "${name}"? It will be hidden but its history is kept.`)) {
    return;
  }
  await act(deleteProject, id);
}
</script>

<template>
  <PageLayout>
    <div class="grid gap-6">
      <header>
        <h1 class="text-2xl font-extrabold text-neutral-900">
          Projects
        </h1>
        <p class="mt-1 text-neutral-500">
          Active projects across the org. Search, filter and sort below; archive
          or delete from each project's menu.
        </p>
      </header>

      <div class="flex flex-wrap items-center justify-between gap-3">
        <p class="text-xs font-semibold uppercase tracking-wide text-neutral-400">
          Library
        </p>
        <button
          type="button"
          class="rounded-md bg-brand-primary px-4 py-2 text-sm font-semibold text-brand-primary-on transition hover:opacity-90"
          @click="showNew = !showNew"
        >
          + New project
        </button>
      </div>

      <!-- New project form -->
      <section
        v-if="showNew"
        class="rounded-xl border border-neutral-200 bg-neutral-0 p-6"
      >
        <h2 class="mb-4 font-semibold">
          New project
        </h2>
        <div class="grid gap-4 sm:grid-cols-3">
          <label class="grid gap-1 text-sm">
            <span class="text-neutral-500">Name *</span>
            <input
              v-model="form.name"
              type="text"
              placeholder="MMA-122"
              class="rounded-md border border-neutral-300 px-3 py-2"
            >
          </label>
          <label class="grid gap-1 text-sm">
            <span class="text-neutral-500">Client</span>
            <input
              v-model="form.client_name"
              type="text"
              placeholder="Midland Metro Alliance"
              class="rounded-md border border-neutral-300 px-3 py-2"
            >
          </label>
          <label class="grid gap-1 text-sm">
            <span class="text-neutral-500">Code</span>
            <input
              v-model="form.code"
              type="text"
              placeholder="MMA-122"
              class="rounded-md border border-neutral-300 px-3 py-2"
            >
          </label>
        </div>
        <p
          v-if="formError"
          class="mt-3 text-sm text-semantic-danger"
        >
          {{ formError }}
        </p>
        <button
          type="button"
          class="mt-4 rounded-md bg-brand-primary px-4 py-2 font-semibold text-brand-primary-on transition hover:opacity-90 disabled:opacity-50"
          :disabled="creating"
          @click="create"
        >
          {{ creating ? "Creating…" : "Create & open" }}
        </button>
      </section>

      <!-- search + tabs -->
      <div class="flex flex-wrap items-center gap-4">
        <input
          v-model="search"
          type="search"
          placeholder="Search projects…"
          class="grow rounded-md border border-neutral-300 px-3 py-2 text-sm"
        >
        <div class="flex gap-1">
          <button
            v-for="t in (['active', 'archived', 'all'] as const)"
            :key="t"
            type="button"
            class="rounded-lg px-3 py-1.5 text-sm font-medium capitalize"
            :class="tab === t
              ? 'bg-brand-primary/10 text-brand-primary'
              : 'text-neutral-500 hover:bg-neutral-50'"
            @click="tab = t"
          >
            {{ t }} ({{ counts[t] }})
          </button>
        </div>
      </div>

      <div
        v-if="isPending"
        class="text-neutral-500"
      >
        Loading…
      </div>
      <div
        v-else-if="isError"
        class="rounded-md bg-semantic-danger/10 p-4 text-semantic-danger"
      >
        Failed to load: {{ error?.message }}
      </div>
      <div
        v-else
        class="grid gap-3"
      >
        <p
          v-if="!filtered.length"
          class="rounded-xl border border-dashed border-neutral-300 p-6 text-center text-neutral-400"
        >
          No {{ tab === "all" ? "" : tab }} projects.
        </p>
        <div
          v-for="p in filtered"
          :key="p.id"
          class="flex items-center gap-4 rounded-xl border border-neutral-200 bg-neutral-0 p-5"
        >
          <button
            type="button"
            class="grow text-left"
            @click="enter(p.id)"
          >
            <h3 class="font-semibold text-neutral-900 hover:text-brand-primary">
              {{ p.name }}
            </h3>
            <p class="mt-1 text-sm text-neutral-500">
              <span v-if="p.client_name">{{ p.client_name }} · </span>
              <span v-if="p.code">{{ p.code }} · </span>
              <span
                :class="p.status === 'archived' ? 'text-neutral-400' : 'text-semantic-success'"
              >{{ p.status }}</span>
            </p>
          </button>

          <!-- per-project menu -->
          <div class="relative">
            <button
              type="button"
              class="rounded-lg p-2 text-neutral-500 hover:bg-neutral-50"
              aria-label="Project menu"
              @click="openMenu = openMenu === p.id ? null : p.id"
            >
              <svg
                class="h-5 w-5"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <circle
                  cx="12"
                  cy="5"
                  r="1.6"
                />
                <circle
                  cx="12"
                  cy="12"
                  r="1.6"
                />
                <circle
                  cx="12"
                  cy="19"
                  r="1.6"
                />
              </svg>
            </button>
            <div
              v-if="openMenu === p.id"
              class="absolute right-0 z-10 mt-1 w-40 rounded-lg border border-neutral-200 bg-neutral-0 py-1 shadow-md"
            >
              <button
                v-if="p.status !== 'archived'"
                type="button"
                class="block w-full px-4 py-2 text-left text-sm hover:bg-neutral-50"
                @click="act(archiveProject, p.id)"
              >
                Archive
              </button>
              <button
                v-else
                type="button"
                class="block w-full px-4 py-2 text-left text-sm hover:bg-neutral-50"
                @click="act(restoreProject, p.id)"
              >
                Restore
              </button>
              <button
                type="button"
                class="block w-full px-4 py-2 text-left text-sm text-semantic-danger hover:bg-neutral-50"
                @click="onDelete(p.id, p.name)"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </PageLayout>
</template>
